import cx from 'classnames';
import Info from 'components/Info';
import React, { useEffect, useRef, useState } from 'react';
import withField from '../withField';
import styles from './styles.css';

type Section = {
  label: string;
  ranges: { min: number; max: number };
  active: {
    color: string;
    info: string;
  };
};

type Tick = {
  tick: number;
  label: string;
};

type Props = {
  name?: string;
  initialValue: number;
  limits: { min: number; max: number };
  sections?: Section[];
  ticks?: Tick[];
  minLabel?: string;
  maxLabel?: string;
  onChange: (x: number) => void;
  normalize?: (x: number) => number;
  maskValue?: (x: number) => number;
  denormalize?: (x: number) => number;
  wrapperStyle?: {};
  sliderStyle?: {};
  showLimits?: boolean;
};

const isCloseTo = (val, limit) => {
  return Math.abs(limit - val) <= 10;
};

const computeSectionPercentageWidth = (
  minRange: number,
  maxRange: number,
  min: number
): string => {
  const normVal = (val) =>
    val < 0 ? Math.abs(val - min) : Math.abs(min) + 1 + val;
  const normMin = normVal(minRange);
  const normMax = normVal(maxRange);
  return `${normMax - normMin}%`;
};

const getStepsUsed = (val, min: number) => {
  const steps = Math.abs(val - min);
  return min <= 0 ? steps + 1 : steps;
};

const Slider: React.FC<Props> = ({
  name,
  initialValue,
  limits,
  showLimits = true,
  sections,
  ticks,
  onChange,
  wrapperStyle = {},
  sliderStyle = {},
  normalize,
  denormalize,
  maskValue,
}) => {
  // const [initVal, setInitVal] = useState<number>(initialValue || 0);
  const [val, setVal] = useState<number>();
  const [valPreviewPos, setValPreviewPos] = useState<number>();
  const ref = useRef(null);

  useEffect(() => {
    if (!val || val !== 0) {
      setVal(initialValue);
    }
  }, []);

  useEffect(() => {
    if (!!val || val === 0) {
      updateValPreviewPos(val);
    }
  }, [val]);

  const { min, max } = limits;
  const steps = 1 + Math.abs(max) - min;

  const onChangeHandler = (newVal) => {
    const newValue = newVal > max ? max : newVal < min ? min : newVal;
    setVal(newValue);
    onChange(normalize ? normalize(newValue) : newValue);
  };

  const updateValPreviewPos = (val) => {
    //@ts-ignore
    const widthPerValue = ref?.current?.offsetWidth / steps;
    const stepsUsed = getStepsUsed(val, min);
    const previewPos = widthPerValue * stepsUsed;
    setValPreviewPos(previewPos);
  };

  return (
    <div className={cx(styles.container)} style={wrapperStyle}>
      {showLimits && (
        <div className={cx(styles.infos, styles.limits)}>
          <label>{!isCloseTo(val, min) && min}</label>
          <label>{!isCloseTo(val, max) && max}</label>
        </div>
      )}
      <div className={cx(styles.sliderWrapper)}>
        <div
          className={cx(styles.valuePreview)}
          style={{ left: valPreviewPos }}
        >
          <div className={cx(styles.triangleDown)}></div>
          {!maskValue ? (
            <input
              type="number"
              value={val}
              onChange={(e) => onChangeHandler(e.target.value)}
            />
          ) : (
            <div>{maskValue(val)}</div>
          )}
        </div>
        <input
          ref={ref}
          name={name}
          className={cx(styles.slider)}
          style={sliderStyle}
          type="range"
          value={val}
          min={min}
          max={max}
          onChange={(e) => onChangeHandler(e.target.value)}
        />
      </div>
      <div className={cx(styles.infos, styles.details)}>
        {sections ? (
          sections.map(({ label, ranges, active }) => {
            const isActive = val >= ranges.min && val <= ranges.max && active;
            return (
              <div
                key={label}
                style={{
                  flex: computeSectionPercentageWidth(
                    ranges.min,
                    ranges.max,
                    min
                  ),
                  ...(isActive
                    ? { borderTopColor: active?.color, color: active?.color }
                    : {}),
                }}
              >
                <label>
                  {label}
                  {isActive && active.info && <Info text={active.info} />}
                </label>
              </div>
            );
          })
        ) : ticks ? (
          <div className={cx(styles.ticks)}>
            {ticks.map(({ label, tick }) => {
              return (
                <div key={tick}>
                  <label>{label}</label>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ flex: '100%' }}></div>
        )}
      </div>
    </div>
  );
};

export default withField(Slider);
